import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { useApiFetch } from '../../shared/api'
import type { MemberInvite } from '../teams/inviteInput'

/** A persisted stand on a drive (CLUB-157/158): the picked map-pin id and the
 *  assigned hunter (null = unassigned). Mirrors the backend `DraftStandDto` and
 *  the mobile/Firebase `MapPinShort { id, userId }`. */
export interface DraftStandDetail {
  pinId: string
  assignedUserId: string | null
}

export interface DraftDriveDetail {
  name: string
  /** Retired single base-area field. Never sent on save anymore — only read on
   *  load, for a draft saved before `subAreaIds` existed; a present
   *  `subAreaIds` (even `[]`) always wins over this. Optional because the save
   *  path no longer populates it. */
  area?: string | null
  standCount: number
  /** Optional for back-compat: drafts created before CLUB-157 have `standCount`
   *  but no persisted stands. */
  stands?: DraftStandDetail[]
  /** Sub-area ids this drive is based on — the current contract, mirroring the
   *  backend `DraftDriveDto.SubAreaIds`. Absent on a draft saved before this
   *  field existed, in which case the legacy `area` is the source of truth. */
  subAreaIds?: string[]
  /** Pin ids visible for this drive — the current contract, mirroring
   *  `DraftDriveDto.PinIds`. Absent on a draft saved before this field existed. */
  pinIds?: string[]
}

export interface HuntableGameClassDetail {
  name: string
  /** Per-class quota; null/absent = "No limit". */
  limit?: number | null
}

export interface HuntableGameSelectionDetail {
  species: string
  /** Chosen classes, each with its own quota (per-category quotas, CLUB-590). */
  classes: HuntableGameClassDetail[]
}

export interface AttachmentFileDetail {
  fileName: string
  storagePath: string
  mimeType: string | null
  sizeBytes: number | null
  /** See `HuntAttachmentFile.downloadUrl` — absent on drafts saved before it existed. */
  downloadUrl?: string | null
}

export interface DraftHuntDetail {
  id: string
  groupId: string
  groupName: string
  huntName: string | null
  startTime: string | null
  endTime: string | null
  leaderIds: string[]
  lastStepIndex: number
  lastStepCompleted: string | null
  participantIds: string[]
  invitationMessage: string | null
  attachmentDocumentIds: string[]
  attachmentFiles: AttachmentFileDetail[]
  drives: DraftDriveDetail[]
  huntableGames: HuntableGameSelectionDetail[]
  /** Estate map-visibility filter: which sub-areas and pins the hunt map shows.
   *  An empty array in a dimension means it is unfiltered (everything shows).
   *  Absent on drafts saved before it existed and on club (non-estate) hunts. */
  estateMapFilter?: EstateMapFilterDetail
  /** Whether hunters see only the planned drives on the map (true) or the whole
   *  hunting ground (false, the default). The API always returns this — never
   *  undefined — so a draft saved before the field existed reads as false. */
  restrictMapToPlannedDrives: boolean
  /** Guests invited by email or phone, persisted on the plan so the wizard shows
   *  them again when the draft is reopened. Absent on drafts saved before the
   *  field existed; empty when the draft has no guests. */
  guests?: MemberInvite[]
}

/** The estate map-visibility filter as it round-trips through the .NET API. */
export interface EstateMapFilterDetail {
  visibleSubAreaIds: string[]
  /** Visible pin ids — the current contract, mirroring
   *  `EstateMapFilterDto.VisiblePinIds`. */
  visiblePinIds: string[]
  /** The retired pin-TYPE picker selection. Read for back-compat; nothing in
   *  web relies on it going forward. */
  visiblePinTypeIds: string[]
}

export function useDraftHunt(groupId: string | null, draftId: string | null) {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'groups', groupId, 'hunts', 'drafts', draftId, uid],
    enabled: !!uid && !!groupId && !!draftId,
    queryFn: () => call<DraftHuntDetail>(
      `/clubhouse/groups/${encodeURIComponent(groupId!)}/hunts/drafts/${encodeURIComponent(draftId!)}`,
    ),
  })
}
